import React from 'react';
import {Trans, useTranslation} from 'react-i18next';
import {useNavigate} from 'react-router-dom';
import ChekinProSettings from '../../ChekinProSettings';
import {SHOWED_GUEST_GROUP_MODAL, COUNTRY_CODES} from 'utils/constants';
import {PATHS} from 'Routes';
import {
  useActions,
  useGuestGroup,
  useModalControls,
  useModalOncePerSession,
  usePath,
  useScreenResize,
} from 'hooks';
import {device} from '@guestapp/ui/src/utils/device';
import {isAllianzFeatureActivated} from 'utils/common';
import {useSchema} from 'context/guest';
import {useSummary} from 'context/summary';
import {useReservation} from 'context/reservation';
import {useWhoIsComingModal} from 'components/WhoComingModalSheet';
import iconRegister from 'assets/icons/empty-state.svg';
import allianzBannerImg from 'assets/img/allianz-banner.png';
import Page from '../../components/Page';
import GuestsPanel from 'components/GuestsPanel';
import ModalSheet from 'components/common/ModalSheet';
import ShareRegistrationLink from 'components/ModalSheets/ShareRegistrationLink';
import {AllianzInsurancesInCarButton} from './components';
import {CustomIcon} from '@guestapp/ui';
import {MainButton} from 'styled/common';
import {
  ButtonsWrapper,
  ContentInformationBox,
  EditIcon,
  GuestListTitle,
  GuestPanelContainer,
  RegisterBottom,
  RegistrationIcon,
  ShareRegistrationLinkButton,
  SubTitle,
  SubTitleInformation,
  Title,
  Wrapper,
} from './styled';

function GuestRegistrationView() {
  const {t} = useTranslation();
  const navigate = useNavigate();
  const {housing, data: reservation} = useReservation();
  const {isGuestRegistrationOnly, isGuestRegistrationComplete} = useSummary();
  const {guestGroup, isLoading} = useGuestGroup();
  const {getFullPath} = usePath();
  const {data: summary} = useSummary();
  const {isMatch: isMobile} = useScreenResize(device.tabletSubHeader);
  const isAllianzActive = isAllianzFeatureActivated(
    reservation?.check_in_date,
    housing?.allianz_status,
    summary?.country_code,
    summary?.nights_of_stay,
  );
  const {resetAllIVState} = useActions();

  const {
    identityVerificationDetails: {enabled: ivEnabled},
  } = useSchema();
  const {isBiometricMatchForAll} = useReservation();

  const {
    isOpen: isOpenShareRegistrationLink,
    closeModal: closeShareRegistrationLink,
    openModal: openShareRegistrationLink,
  } = useModalControls();

  const {shouldOpenModal} = useModalOncePerSession({
    id: reservation?.id,
    storageKey: SHOWED_GUEST_GROUP_MODAL,
  });

  const {
    modal: WhoIsComingModal,
    openModal,
    ageLimit,
  } = useWhoIsComingModal({
    openModalPerSession: shouldOpenModal,
    showFirstTime: isGuestRegistrationOnly,
  });

  const hasNoGuests = guestGroup?.added_number_of_guests === 0;
  const totalGuest = guestGroup?.known_number_of_guests || 5;
  const currentTotalGuest = guestGroup?.added_number_of_guests || 0;
  const isRegisterGuestButtonVisible =
    ChekinProSettings.isEmbedded && !ChekinProSettings.canEditReservationDetails
      ? !isGuestRegistrationComplete
      : true;

  const nextPage = React.useMemo(() => {
    const goToIv =
      (ivEnabled && !isBiometricMatchForAll && hasNoGuests) || isBiometricMatchForAll;
    const goToForm = !ivEnabled || (ivEnabled && !isBiometricMatchForAll && !hasNoGuests);

    if (goToIv) {
      return getFullPath(PATHS.verification.main);
    }
    if (goToForm) {
      return getFullPath(PATHS.addPersonalDataForm);
    }
    return getFullPath(PATHS.verification.main);
  }, [ivEnabled, getFullPath, isBiometricMatchForAll, hasNoGuests]);

  const onBackClick = () => {
    navigate(getFullPath(PATHS.home), {replace: true});
  };

  const handleRegisterClick = React.useCallback(() => {
    resetAllIVState();
  }, [resetAllIVState]);

  const TitleComponent = React.useCallback(
    () => (
      <GuestListTitle className="guest-register-view__title">
        {t('registered_guests')} ({currentTotalGuest} {t('of')} {totalGuest})
        {ChekinProSettings.canEditReservationDetails && (
          <EditIcon onClick={openModal}>
            <CustomIcon name="editIcon" title="Edit" size={24} color="rgb(57, 58, 189)" />
          </EditIcon>
        )}
      </GuestListTitle>
    ),
    [currentTotalGuest, openModal, t, totalGuest],
  );
  const minorsText = (
    <ContentInformationBox>
      <CustomIcon size={16} name="information" />
      <SubTitleInformation>
        <Trans i18nKey="only_guest_over" values={{ageLimit}}>
          Only guests over {ageLimit} years old need to be registered
        </Trans>
      </SubTitleInformation>
    </ContentInformationBox>
  );
  const handleBannerClick = React.useCallback(() => {
    const existGuestFromSpain = guestGroup?.members.some(
      member => member.residence_country === COUNTRY_CODES.spain,
    );
    const path = existGuestFromSpain
      ? `../${PATHS.travelInsurances.main}/${PATHS.travelInsurances.allianz.main}/${PATHS.travelInsurances.allianz.selectGuests}`
      : `../${PATHS.travelInsurances.main}/${PATHS.travelInsurances.allianz.main}/${PATHS.travelInsurances.allianz.createGuest}`;
    navigate(path);
  }, [guestGroup, navigate]);

  const banner = isAllianzActive
    ? {
        src: allianzBannerImg,
        onClick: handleBannerClick,
      }
    : undefined;

  const validateAgeLimit = ageLimit !== '0';

  return (
    <Page
      isSubHeaderVisible
      title={t('guest_summary')}
      isLoading={isLoading}
      onBackButtonClick={onBackClick}
      banner={banner}
      navBarTitle={isMobile ? t('guest_summary') : t('online_chekin')}
      className="guest-register-view"
    >
      <Wrapper>
        {hasNoGuests ? (
          <>
            <TitleComponent />
            {validateAgeLimit && minorsText}
            <RegistrationIcon src={iconRegister} />
            <Title className="guest-register-view__no-guests-title">
              {t('no_registered_guest')}
            </Title>
            <SubTitle className="guest-register-view__no-guests-subtitle">
              {t('click_register_guest')}
            </SubTitle>
            <ButtonsWrapper>
              <RegisterBottom to={nextPage}>{t('register_guest')}</RegisterBottom>
              {ChekinProSettings.canShareRegistrationLink && (
                <ShareRegistrationLinkButton onClick={openShareRegistrationLink}>
                  {t('share_registration_link')}
                </ShareRegistrationLinkButton>
              )}
            </ButtonsWrapper>
          </>
        ) : (
          <>
            <TitleComponent />
            {validateAgeLimit && minorsText}
            <GuestPanelContainer className="guest-register-view__guest-panel">
              <GuestsPanel />
              {isAllianzActive && <AllianzInsurancesInCarButton />}
            </GuestPanelContainer>
            {isRegisterGuestButtonVisible && (
              <MainButton
                to={nextPage}
                onClick={handleRegisterClick}
                label={t('register_another_guest')}
                className="guest-register-view__register-btn"
              />
            )}
            {ChekinProSettings.canShareRegistrationLink && (
              <ShareRegistrationLinkButton
                onClick={openShareRegistrationLink}
                className="guest-register-view__share-btn"
              >
                {t('share_registration_link')}
              </ShareRegistrationLinkButton>
            )}
          </>
        )}
      </Wrapper>
      <ModalSheet
        isOpenModalSheet={isOpenShareRegistrationLink}
        handleCloseModal={closeShareRegistrationLink}
        // title={<>t('share_registration_link_title')}
        maxWidth={'375px'}
      >
        <ShareRegistrationLink />
      </ModalSheet>
      <WhoIsComingModal />
    </Page>
  );
}

export {GuestRegistrationView};
